import { MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiErrorMessage } from './api/error-message';
import { callerReportsErrors } from './api/write-mutation';
import { ApiError } from './api/errors';

/**
 * A module-level singleton rather than a per-render instance.
 *
 * Route guards run in `clientLoader`, which is outside React — they need to
 * reach the same cache the components read from. That is safe here in a way it
 * would not be under SSR: one browser tab, one user, one client.
 */
/**
 * Every write that fails says so, unless its caller is saying it instead.
 *
 * The default before this was silence: a mutation with no `onError` failed and
 * the screen carried on as though nothing had happened, which for "approve this
 * extraction" or "clock in" means pressing the button again. The exception is a
 * call that used `mutateAsync` — awaiting is the caller taking responsibility,
 * and it is the forms, which put the message under the field it belongs to.
 * See useWriteMutation.
 */
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    if (callerReportsErrors(mutation.options.meta)) return;

    toast.error(apiErrorMessage(error));
  },
});

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      /**
       * Never retry what will not succeed. A 401 has already been through the
       * API client's refresh-and-replay by the time it lands here, and 4xx in
       * general means the request itself is wrong.
       */
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          if (error.isNetworkError) return failureCount < 2;
          if (error.status >= 400 && error.status < 500) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
